import { useMemo, useState } from 'react';
import type { Contact, RecipientOrganization } from '~/utils/contact';

export type SellerSelection<TOrg extends RecipientOrganization> = {
  organization: TOrg;
  contacts: Contact[];
};

type SellerInvitation = {
  sellingOrganizationId: string;
  recipientUserIds: string[];
  primaryContactUserId?: string;
};

type PickedOrganizationSnapshot<TOrg> = {
  organization: TOrg;
  contacts: Contact[];
  primaryContactId?: string;
};

type Selection<TOrg> = Map<string, PickedOrganizationSnapshot<TOrg>>;

type TurnedOnSellerList = {
  sellerIds: ReadonlySet<string>;
  addedSellerIds: ReadonlySet<string>;
};

type RecipientsState<TOrg> = {
  selection: Selection<TOrg>;
  turnedOnSellerLists: ReadonlyMap<string, TurnedOnSellerList>;
};

export type ContactSelection = {
  contactIds: string[];
  primaryContactId: string | undefined;
};

function withSelections<TOrg extends RecipientOrganization>(
  state: RecipientsState<TOrg>,
  selections: SellerSelection<TOrg>[]
): RecipientsState<TOrg> {
  const selection = new Map(state.selection);
  selections
    .filter(({ organization }) => !selection.has(organization.id))
    .forEach(({ organization, contacts }) =>
      selection.set(organization.id, { organization, contacts })
    );
  return { ...state, selection };
}

function withOrganizations<TOrg extends RecipientOrganization>(
  state: RecipientsState<TOrg>,
  organizations: TOrg[]
): RecipientsState<TOrg> {
  return withSelections(
    state,
    organizations.map((organization) => ({ organization, contacts: [] }))
  );
}

function withoutOrganizations<TOrg extends RecipientOrganization>(
  state: RecipientsState<TOrg>,
  organizationIds: string[]
): RecipientsState<TOrg> {
  const selection = new Map(state.selection);
  organizationIds.forEach((organizationId) => selection.delete(organizationId));
  const turnedOnSellerLists = new Map(
    [...state.turnedOnSellerLists].filter(
      ([, { sellerIds }]) => !organizationIds.some((id) => sellerIds.has(id))
    )
  );
  return { selection, turnedOnSellerLists };
}

export function useSellerRecipients<
  TOrg extends RecipientOrganization = RecipientOrganization,
>() {
  const [state, setState] = useState<RecipientsState<TOrg>>(() => ({
    selection: new Map(),
    turnedOnSellerLists: new Map(),
  }));
  const { selection, turnedOnSellerLists } = state;

  const toggleOrganization = (organization: TOrg) => {
    setState((previous) =>
      previous.selection.has(organization.id)
        ? withoutOrganizations(previous, [organization.id])
        : withOrganizations(previous, [organization])
    );
  };

  const selectOrganizations = (organizations: TOrg[]) => {
    setState((previous) => withOrganizations(previous, organizations));
  };

  const deselectOrganizations = (organizationIds: string[]) => {
    setState((previous) => withoutOrganizations(previous, organizationIds));
  };

  const chooseContacts = (
    organization: TOrg,
    contacts: Contact[],
    primaryContactId: string | undefined
  ) => {
    setState((previous) => ({
      ...previous,
      selection: new Map(previous.selection).set(organization.id, {
        organization,
        contacts,
        primaryContactId,
      }),
    }));
  };

  const turnOnSellerList = (
    sellerListId: string,
    selections: SellerSelection<TOrg>[]
  ) => {
    setState((previous) => {
      const next = withSelections(previous, selections);
      const sellerIds = selections.map(({ organization }) => organization.id);
      const addedSellerIds = sellerIds.filter(
        (id) => !previous.selection.has(id)
      );
      const alreadyAdded =
        previous.turnedOnSellerLists.get(sellerListId)?.addedSellerIds ?? [];
      return {
        ...next,
        turnedOnSellerLists: new Map(next.turnedOnSellerLists).set(
          sellerListId,
          {
            sellerIds: new Set(sellerIds),
            addedSellerIds: new Set([...alreadyAdded, ...addedSellerIds]),
          }
        ),
      };
    });
  };

  const turnOffSellerList = (sellerListId: string) => {
    setState((previous) => {
      const turnedOn = new Map(previous.turnedOnSellerLists);
      const addedSellerIds = turnedOn.get(sellerListId)?.addedSellerIds ?? [];
      turnedOn.delete(sellerListId);
      const removedIds: string[] = [];
      addedSellerIds.forEach((id) => {
        const holder = [...turnedOn].find(([, { sellerIds }]) =>
          sellerIds.has(id)
        );
        if (holder) {
          const [holderId, holderList] = holder;
          turnedOn.set(holderId, {
            ...holderList,
            addedSellerIds: new Set([...holderList.addedSellerIds, id]),
          });
        } else {
          removedIds.push(id);
        }
      });
      return withoutOrganizations(
        { ...previous, turnedOnSellerLists: turnedOn },
        removedIds
      );
    });
  };

  const isSellerListOn = (sellerListId: string) =>
    turnedOnSellerLists.has(sellerListId);

  const selectedOrganizations = useMemo(
    () => [...selection.values()],
    [selection]
  );

  const selectedOrganizationIds = useMemo(
    () => new Set(selection.keys()),
    [selection]
  );

  const isOrganizationSelected = (organizationId: string) =>
    selection.has(organizationId);

  const contactSelectionOf = (organizationId: string): ContactSelection => {
    const picked = selection.get(organizationId);
    return {
      contactIds: picked ? picked.contacts.map(({ id }) => id) : [],
      primaryContactId: picked ? picked.primaryContactId : undefined,
    };
  };

  const sellerInvitations: SellerInvitation[] = useMemo(
    () =>
      selectedOrganizations.map(
        ({ organization, contacts, primaryContactId }) => ({
          sellingOrganizationId: organization.id,
          recipientUserIds: contacts.map((contact) => contact.id),
          ...(primaryContactId && { primaryContactUserId: primaryContactId }),
        })
      ),
    [selectedOrganizations]
  );

  return {
    selectedOrganizationIds,
    isOrganizationSelected,
    contactSelectionOf,
    chooseContacts,
    toggleOrganization,
    selectOrganizations,
    deselectOrganizations,
    isSellerListOn,
    turnOnSellerList,
    turnOffSellerList,
    selectedOrganizations,
    sellerInvitations,
  };
}

export type SellerRecipients<
  TOrg extends RecipientOrganization = RecipientOrganization,
> = ReturnType<typeof useSellerRecipients<TOrg>>;
